import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ContentCopy, Delete, Edit } from "@mui/icons-material";
import { RateTable } from "../../../graphql/generated";
import { useTranslation } from "react-i18next";
import { RatingTableItem } from "./RatingTableEditor";
import { testIds } from "./RatingTablesList.testIds";

type RatingTablesListProps = {
  ratingTables: RateTable[];
  onEdit: (table: RatingTableItem) => void;
  onCopy: (table: RatingTableItem) => void;
  onDelete: (tableId: string) => void;
  onCreateNew: () => void;
  deleting: boolean;
};

const RatingTablesList = ({
  ratingTables,
  onEdit,
  onCopy,
  onDelete,
  onCreateNew,
  deleting,
}: RatingTablesListProps) => {
  const { t } = useTranslation("billingRules");

  // Strip server-only fields so a table can be reopened in the editor.
  const toEditableItem = (table: RateTable): RatingTableItem => {
    const {
      createdAt,
      updatedAt,
      entityId,
      entityType,
      customer,
      driver,
      ...ratingTableItem
    } = table;
    return ratingTableItem;
  };

  const handleEdit = (table: RateTable) => {
    onEdit(toEditableItem(table));
  };

  const handleCopy = (table: RateTable) => {
    onCopy(toEditableItem(table));
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">
          {t("ratingTables", "Rating Tables")}
        </Typography>
        <Button data-testid={testIds.ratingTablesListCreateRatingTable} variant="contained" color="primary" onClick={onCreateNew}>
          {t("createRatingTable", "Create Rating Table")}
        </Button>
      </Box>

      {ratingTables.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              {t(
                "noRatingTables",
                "No rating tables found. Create your first rating table to get started."
              )}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {ratingTables.map((table) => (
            <Grid item xs={12} md={6} lg={4} key={table._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {table.label || t("unnamedTable", "Unnamed Table")}
                  </Typography>
                  {table.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {table.description}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("billingType", "Billing Type")}:</strong>{" "}
                    {table.rateType}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("dimensions", "Dimensions")}:</strong>{" "}
                    {table.columnAxis ? "2D" : "1D"} (
                    {table.rowAxis.conditions.length} x{" "}
                    {table.columnAxis?.conditions.length || 1})
                  </Typography>
                  {table.referenceNumber && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{t("form.reference", "Reference")}:</strong>{" "}
                      {table.referenceNumber}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color={table.isActive ? "success.main" : "error.main"}
                  >
                    <strong>{t("status", "Status")}:</strong>{" "}
                    {table.isActive
                      ? t("active", "Active")
                      : t("inactive", "Inactive")}
                  </Typography>
                </CardContent>
                <CardActions
                  sx={{ justifyContent: "space-between", px: 2, pb: 2 }}
                >
                  <Button data-testid={`${testIds.ratingTablesListEdit}-${table._id}`}
                    startIcon={<Edit />}
                    variant="outlined"
                    size="small"
                    onClick={() => handleEdit(table)}
                  >
                    {t("edit", "Edit")}
                  </Button>
                  <Box>
                    <IconButton
                      data-testid={`${testIds.ratingTablesListCopy}-${table._id}`}
                      size="small"
                      title={t("copyRatingTable", "Copy this rating table")}
                      onClick={() => handleCopy(table)}
                    >
                      <ContentCopy />
                    </IconButton>
                    <IconButton data-testid={`${testIds.ratingTablesListDelete}-${table._id}`}
                      color="error"
                      size="small"
                      onClick={() => onDelete(table._id)}
                      disabled={deleting}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default RatingTablesList;
